// Dates and small formatting helpers. UK conventions throughout (en-GB).

const DAY = 86_400_000;

export const pad2 = (n: number) => String(n).padStart(2, "0");
export const today0 = (): Date => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};
export const isoDate = (d: Date) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
export const parseIso = (s: string) => {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y!, (m ?? 1) - 1, d ?? 1);
};

/** "4 Nov 2026" */
export const fmtDate = (s: string) =>
  parseIso(s).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

/** Unix seconds → "22 Oct 09:14" (same year) or "22 Oct 2025". */
export function fmtStamp(ts: number): string {
  const d = new Date(ts * 1000);
  const sameYear = d.getFullYear() === new Date().getFullYear();
  const day = `${d.getDate()} ${d.toLocaleString("en-GB", { month: "short" })}`;
  return sameYear ? `${day} ${pad2(d.getHours())}:${pad2(d.getMinutes())}` : `${day} ${d.getFullYear()}`;
}

/** Unix seconds → "Today 09:14" / "Yesterday" / "19 Oct". */
export function fmtLastSeen(ts: number | null): string {
  if (!ts) return "Not signed in";
  const d = new Date(ts * 1000);
  const days = Math.round((today0().getTime() - new Date(d).setHours(0, 0, 0, 0)) / DAY);
  if (days === 0) return `Today ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
  if (days === 1) return "Yesterday";
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

/** Unix seconds → "13 Oct 2026". */
export const fmtDay = (ts: number) =>
  new Date(ts * 1000).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

/** Seconds from now → "18h", "6 days", "40 min". */
export function fmtIn(seconds: number): string {
  if (seconds < 3600) return `${Math.max(1, Math.round(seconds / 60))} min`;
  if (seconds < 86_400 * 2) return `${Math.round(seconds / 3600)}h`;
  return `${Math.round(seconds / 86_400)} days`;
}

export function fmtAgo(ts: number, nowS = Date.now() / 1000): string {
  const s = nowS - ts;
  if (s < 90) return "just now";
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86_400) return `${Math.round(s / 3600)}h ago`;
  if (s < 86_400 * 2) return "yesterday";
  return new Date(ts * 1000).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

export function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter((w) => /^[A-Za-z]/.test(w))
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase() || "?";

export interface ExpiryInfo {
  days: number;
  status: "expired" | "soon" | "ok";
  date: string;
  tone: "danger" | "warning" | "neutral";
  short: string;
  text: string;
}

/** Expiry status for an item: expired (<0), soon (≤ remind days), or ok. */
export function expiryInfo(item: { expires?: string; remind?: number }, today = today0()): ExpiryInfo | null {
  if (!item.expires || !/^\d{4}-\d{2}-\d{2}$/.test(item.expires)) return null;
  const days = Math.round((parseIso(item.expires).getTime() - today.getTime()) / DAY);
  const remind = item.remind || 30;
  const status = days < 0 ? "expired" : days <= remind ? "soon" : "ok";
  return {
    days,
    status,
    date: fmtDate(item.expires),
    tone: status === "expired" ? "danger" : status === "soon" ? "warning" : "neutral",
    short: days < 0 ? "Expired" : days === 0 ? "Today" : `${days}d`,
    text:
      days < 0
        ? `Expired ${-days} ${days === -1 ? "day" : "days"} ago`
        : days === 0
          ? "Expires today"
          : `In ${days} ${days === 1 ? "day" : "days"}`,
  };
}

/** "1 item", "3 items". */
export const nItems = (n: number): string => `${n} ${n === 1 ? "item" : "items"}`;
